import { Component } from '@verndale/core';
import Pristine from 'pristinejs';
import GeneralForm from './general-form-row';
import StepForm from './step-form';
import serialize from 'form-serialize';
import Cookies from 'js-cookie';
import { getTimesConfig, buildOptionsHtml } from '../helpers/times';

import { selectors } from '../analytics-helpers';

class Module extends Component {
  setupDefaults() {
    this.dom = {
      $form: this.el,
      $error: this.el._$('.form-error'),
      $submit: this.el._$('[type="submit"]'),
      $fileContainer: this.el._$('.field-file__container'),
      $fileBtnRemove: this.el._$('.field-file__remove'),
      $fileSelected: this.el._$('.field-file__name'),
      $date: this.el._$$('duet-date-picker'),
      $topics: this.el._$$('.community-contact-us__topics select'),
      $loadingRing: this.el._$('.loading-box'),
      $parentNode: this.el.parentNode,
      $requiredWhenVisible: this.el
        ._$$('[data-pristine-required-message-when-visible]')
        .filter(el => !!el.dataset.pristineRequiredMessageWhenVisible)
    };

    this.formEndPoint = this.dom.$form.dataset.endpoint;
    if (this.el.dataset.params) {
      this.queryParams = JSON.parse(this.el.dataset.params);
    }

    if (this.el.dataset.submitParameter) {
      this.submitParameter = this.el.dataset.submitParameter;
    }

    this.locationEndPoint = this.el.dataset.geoEndpoint;
    this.cookiesToSend = ['current_community'];
    this.methodForm = this.el.getAttribute('method');
    this.successClass = 'form--success';
    this.errorClass = 'form--error';
    this.loadingClass = 'form--loading';
    this.defaultError = this.dom.$error?.textContent;
    this.defaultSubmitDisableValue = this.dom.$submit
      ? this.dom.$submit.disabled
      : false;

    this.ajaxSubmit = this.dom.$form.classList.contains('form--ajax');
    this.isFormWithText = this.dom.$parentNode.classList.contains(
      'general-form-row__inner--text'
    );
    this.isSitecoreForm = this.el.classList.contains('sitecore-form');
    this.selectedTopic = '';

    if (this.isSitecoreForm) {
      this.dom.$form = this.el._$('form');
      this.methodForm = this.el._$('form').getAttribute('method');
      new GeneralForm({
        dom: this.dom
      });
      new StepForm({
        dom: this.dom
      });
    }

    if (this.dom.$date) {
      this.setupDate();
    }

    this.validateRequiredFields();
    this.verifyQueryParams();

    const isDropdown = this.el.closest('.primary-nav__child-menu');

    const outputsize = () => {
      if (this.el.offsetHeight > 0) {
        this.validateRequiredFields();
      }
    };

    if (isDropdown) {
      outputsize();
      new ResizeObserver(outputsize).observe(this.el);
    }
  }

  addListeners() {
    this.dom.$form._on('submit', this.handleSubmit.bind(this));
    this.dom.$topics._on('change', () => {
      this.selectedTopic = this.dom.$topics[0]
        .querySelector('option:checked')
        .getAttribute('google-analytics-tracking-code')
        .toLowerCase()
        ? this.dom.$topics[0]
            .querySelector('option:checked')
            .getAttribute('google-analytics-tracking-code')
            .toLowerCase()
        : this.submitParameter;
      setTimeout(() => {
        this.validateRequiredFields();
      });
    });

    this.dom.$form._on('reset', this.handleReset.bind(this));
  }

  setupDate() {
    this.dom.$date.forEach(date => {
      const DATE_FORMAT_US = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

      date.dateAdapter = {
        parse(value = '', createDate) {
          const matches = value.match(DATE_FORMAT_US);

          if (matches) {
            return createDate(matches[3], matches[1], matches[2]);
          }
        },

        format(date) {
          return `${
            date.getMonth() + 1
          }/${date.getDate()}/${date.getFullYear()}`;
        }
      };

      date.localization = {
        buttonLabel: 'Choose date',
        selectedDateMessage: 'Selected date is',
        prevMonthLabel: 'Previous month',
        nextMonthLabel: 'Next month',
        monthSelectLabel: 'Month',
        yearSelectLabel: 'Year',
        closeLabel: 'Close window',
        calendarHeading: 'Choose a date',
        dayNames: [
          'Sunday',
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday'
        ],
        monthNames: [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December'
        ],
        monthNamesShort: [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec'
        ],
        locale: 'en-US',
        placeholder: date.getAttribute('placeholder') || 'mm/dd/yy'
      };
    });
  }

  moveDateInputName() {
    this.dom.$date.forEach(date => {
      const $dateInput = date._$('.duet-date__input');

      if (date.name && $dateInput) {
        $dateInput.name = date.name;
        date.removeAttribute('name');
      }
    });
  }

  validate() {
    this.moveDateInputName();

    this.pristineForm = new Pristine(this.dom.$form, {
      classTo: 'field',
      errorClass: 'field--error',
      successClass: 'field--success',
      errorTextParent: 'field',
      errorTextTag: 'small',
      errorTextClass: this.isSitecoreForm
        ? 'field__error--sitecore-hide'
        : 'field__error'
    });
  }

  validateRequiredFields() {
    if (this.dom.$requiredWhenVisible.length) {
      this.dom.$requiredWhenVisible.forEach(el => {
        el.required = false;

        const shouldValidateDatepicker =
          el.nodeName === 'DUET-DATE-PICKER' &&
          el._$('.duet-date__input')?.offsetWidth > 0 &&
          el._$('.duet-date__input')?.offsetHeight > 0;
        const shouldValidateOthers =
          el.nodeName !== 'DUET-DATE-PICKER' &&
          el.offsetWidth > 0 &&
          el.offsetHeight > 0;

        if (shouldValidateDatepicker || shouldValidateOthers) {
          if (el.nodeName === 'DUET-DATE-PICKER') {
            const error = el.dataset.pristineRequiredMessageWhenVisible;
            const $dateInput = el._$('.duet-date__input');
            $dateInput.dataset.pristineRequiredMessage = error;
            $dateInput.setAttribute('required', '');
          } else {
            el.setAttribute(
              'data-pristine-required-message',
              el.dataset.pristineRequiredMessageWhenVisible
            );
            el.setAttribute('required', '');
          }
        }
      });
    }
    this.validate();
  }

  verifyQueryParams() {
    const urlParams = new URLSearchParams(window.location.search);

    // If the query params are set as a data attr, then we need to set them as cookies and send them as part of the form
    if (this.queryParams && this.queryParams.length > 0) {
      this.queryParams.forEach(param => {
        if (urlParams.has(param)) {
          const cookie = { [param]: urlParams.get(param) };
          Cookies.set(param, JSON.stringify(cookie[param]));
          this.cookiesToSend.push(param);
        } else {
          const keys = Object.keys(Cookies.get());

          keys.forEach(key => {
            if (key.includes(param)) {
              this.cookiesToSend.push(param);
            }
          });
        }
      });
    }
  }

  setLoading(bool) {
    if (!this.isSitecoreForm) {
      this.dom.$form.classList.toggle(this.loadingClass, bool);
      if (!this.dom.$submit) this.dom.$submit = this.el._$('[type="submit"]');
      try {
        bool
          ? this.dom.$submit?.appendChild?.(this.dom.$loadingRing)
          : this.dom.$loadingRing.remove();
      } catch (e) {
        console.log(e);
      }
    }
  }

  validateDateAndTime() {
    const $timesSelect = this.dom.$form.querySelector('[name="preferredTime"]');
    const $dateInput = this.dom.$form.querySelector('.duet-date__input');

    // this form doesn't have a date/time input or is not booking a tour
    if (!$timesSelect || !$dateInput || this.selectedTopic != 'schedulevisit') {
      return true;
    }

    const selectedTime = $timesSelect.value;
    const seletedDate = this.dom.$date[0].value;

    const todayFormated = new Date()
      .toLocaleString('sv', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
      .split(' ')[0]
      .split('/')
      .reverse()
      .join('-');
    const isToday = seletedDate === todayFormated;
    const today = new Date().toLocaleString('en-US', {
      timeZone: 'America/New_York'
    });

    const { parsedOptions, minDatePicker } = getTimesConfig(
      this.dom.$date[0],
      isToday,
      today
    );
    const optionsHtml = buildOptionsHtml(parsedOptions);

    if (seletedDate < minDatePicker) {
      const errorMessage = this.dom.$date[0].dataset.pristineNoTimes;
      $timesSelect.innerHTML = '';
      $timesSelect.pristine.errors = [];
      this.pristineForm.addError($timesSelect, errorMessage);
      $timesSelect.innerHTML = optionsHtml;

      this.dom.$date[0].min = minDatePicker;
      $dateInput.pristine.errors = [];
      this.pristineForm.addError($dateInput, errorMessage);
      return false;
    }

    const validTime = parsedOptions.find(
      option => option.value === selectedTime
    );

    if (!validTime) {
      const errorMessage = $timesSelect.dataset.pristineNoLongerAvailable;
      $timesSelect.innerHTML = '';
      $timesSelect.pristine.errors = [];
      this.pristineForm.addError($timesSelect, errorMessage);
      $timesSelect.innerHTML = optionsHtml;
      return false;
    }

    return true;
  }

  handleSubmit(e) {
    e.preventDefault();

    // Workaround for the pristine form validate method to work together with Sitecore personalize
    window.$ = () => {};

    const date = this.dom.$form.querySelector('.duet-date__input');
    const isValid = this.pristineForm.validate();
    const zipCode = this.dom.$form.querySelector('[name="zipCode"]');
    const nearbyCommunitiesValue = this.dom.$form.querySelector('[name="NearbyCommunities"]')?.value;
    const contextCommunity = this.dom.$form.querySelector('[name="ContextCommunity"]')?.value;

    if (date?.value === '' && date.dataset.pristineRequiredMessage) {
      date.pristine.errors = [];
      this.pristineForm.addError(date, date.dataset.pristineRequiredMessage);
      return;
    }

    if (zipCode && zipCode.value && !nearbyCommunitiesValue && contextCommunity) {
      zipCode.pristine.errors = [];
      this.pristineForm.addError(zipCode, zipCode.dataset.errorMessage);
      return;
    }

    if (!this.validateDateAndTime()) {
      return;
    }

    const headers = {
      'content-type': 'application/json'
    };

    if (!isValid || (!this.methodForm && !this.el.parentNode.classList.contains('episerver-form'))) {
      console.log('invalid form');
      return;
    }

    (() => {
      const topics = Array.from(
        document.querySelectorAll(`${selectors.contactUsFormTopic} option`)
      );

      const selectedTopic = topics && topics.find( option => option.value === document.querySelector(selectors.contactUsFormTopic).value)?.innerText;
    })();

    const token = this.el._$('[name=__RequestVerificationToken]')?.value;

    if (token) {
      headers.__RequestVerificationToken = token;
    }

    if (this.isSitecoreForm) {
      console.log(this.dom.$form.action);

      window.fetch(this.dom.$form.action, {
        method: 'POST',
        body: JSON.stringify(
          serialize(this.dom.$form, {
            hash: true,
            disabled: true,
            empty: true
          })
        ),
        headers
      })
      .then(r => r.json())
      .then(this.handleSuccess.bind(this))
      .catch(this.handleError.bind(this));
    } else {
      this.fetchResults(this.formEndPoint, headers);
      this.setLoading(true);
    }
  }

  async fetchResults(url, headers) {
    const payload = {
      ...serialize(this.dom.$form, {
        hash: true,
        disabled: true,
        empty: true
      })
    };

    // BE needs cookies being sent with the request because of CDN caching
    this.cookiesToSend.forEach(cookie => {
      const cookieValue = Cookies.get(cookie);
      if (cookieValue) {
        try {
          let cookieData = JSON.parse(cookieValue);
          if (cookieData && cookie === 'current_community') {
            cookieData = cookieData.ssl_communityid;
          }
          // BE only needs ID, not the whole object (if needed uncomment community-context)
          // payload['community-context'] = JSON.parse(payload['community-context']);
          payload[cookie] = cookieData;
        } catch (error) {
          let cookieData = cookieValue;
          if (cookieData && cookie === 'current_community') {
            cookieData = cookieData.ssl_communityid;
          }
          // BE only needs ID, not the whole object (if needed uncomment community-context)
          // payload['community-context'] = JSON.parse(payload['community-context']);
          payload[cookie] = cookieData;
        }
      }
    });

    // BE needs the current geolocation and IP to be sent with the request
    if (this.locationEndPoint) {
      const responseGeo = await fetch(this.locationEndPoint);
      const bodyGeoLocation = await responseGeo.json();

      payload['geolocation'] = { ...bodyGeoLocation };
    }

    const response = await fetch(url, {
      method: this.methodForm,
      body: JSON.stringify(payload),
      headers
    });

    if (!response.ok || response.status !== 200) {
      this.handleError(response.Message);
      return;
    }

    const body = await response.json();

    this.setLoading(false);
    this.handleSuccess(body);
  }

  handleReset() {
    this.pristineForm.reset();
    this.dom.$submit.disabled = this.defaultSubmitDisableValue;
  }

  handleSuccess(res) {
    if (this.isSitecoreForm && !this.dom.$form) {
      this.dom.$form = this.el._$('form');
    }

    if (this.dom.$error) {
      this.dom.$error.hidden = true;
    }

    this.el.classList.add(this.successClass);
    this.el.classList.remove(this.errorClass);
    this.el.classList.remove('field-validation-error');

    this.sendGoogleDataEvent();

    if (!this.isSitecoreForm) {
      if (res.redirectUrl) {
        this.successRedirect(res);
        this.handleReset();
        return;
      }
    }

    if (!this.dom.$form.action) {
      this.handleReset();
      return;
    }

    if (res.RedirectUrl) {
      this.successRedirect(res);
      return;
    }

    const event = new CustomEvent('successfullysubmitted', { detail: res });
    this.dom.$form.dispatchEvent(event);
    this.setLoading(false);
  }

  successRedirect(res) {
    if (this.selectedTopic === '') {
      this.selectedTopic = this.submitParameter;
    }
    const url = this.selectedTopic
      ? `${res.redirectUrl}?formtopic=${this.selectedTopic}`
      : res.redirectUrl;
    const { Boxever, _boxever_settings } = window;

    if (!Boxever || !_boxever_settings) {
      window.location = url;
      return;
    }

    const eventToSend = {
      channel: 'WEB',
      type: 'SUCCESSFUL_FORM_SUBMISSION',
      pos: window._boxever_settings.pointOfSale,
      browser_id: Boxever.getID()
    };

    Boxever.eventCreate(
      eventToSend,
      data => {
        window.location = url;
      },
      'json'
    );
  }

  handleError(msg) {
    console.log('handleError', msg?.Message);

    this.el.classList.add(this.errorClass);
    this.el.classList.remove(this.successClass);

    if (this.dom.$error) {
      this.dom.$error.hidden = false;
      this.dom.$error.innerHTML = msg || this.defaultError;
    }

    // For none sitecore forms only
    this.setLoading(false);

    const event = new CustomEvent('submiterror', { detail: msg });
    this.dom.$form.dispatchEvent(event);
  }

  sendGoogleDataEvent() {
    const formData = serialize(this.dom.$form, {
      hash: true,
      disabled: true,
      empty: true
    });

    let selectedTopic = undefined;
    let type = "inquiry";

    if (formData.topic) {
      const topics = Array.from(
        document.querySelectorAll(`${selectors.contactUsFormTopic} option`)
      );

      selectedTopic = topics && topics.find( option => option.value === formData.topic)?.innerText;
    } else if (this.dom.$form.classList[0] === 'schedule-a-tour__form') {
      selectedTopic = 'Booking a Tour';    
    }

    if (selectedTopic === 'Careers' || selectedTopic == 'Volunteering') {
      type = 'non-inquiry';
    }

    const payload = {
      event: 'form_submit',
      detailed_event: 'Form Complete',
      page_data: {
        user_login_state: 'anonymous',
      },
      event_data: {
        identifier: this.dom.$form.classList[0],
        name: this.dom.$form.classList[0],
        type: type,
        form_category: selectedTopic
      },
      user_data: {
        user_id: undefined,
        user_type: 'new',
        user_phone_number: formData.phoneNumber,
        user_email: formData.emailAddress,
        user_first_name: formData.firstName,
        user_last_name:  formData.lastName,
        user_zip: formData.zipCode
      }
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event_data: null, user_data: null });  // Clear the previous event_data and user_data objects.
    window.dataLayer.push(payload);

    console.log('data layer sent', payload);
  }
}

export default Module;
